import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  // Pro add-on state — independent of Premium. See backend routes/pro.py for
  // the schema. During beta the HR panel stays visible to all, but we still
  // track real state so paywall logic can be flipped on later.
  const [isPro, setIsPro] = useState(false);
  const [proStatus, setProStatus] = useState('none');
  const [proTrialDaysLeft, setProTrialDaysLeft] = useState(null);
  const [proTrialExpiresAt, setProTrialExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const [subRes, proRes] = await Promise.all([
        fetch(`${API}/subscription/status`, { credentials: 'include' }),
        fetch(`${API}/pro/status`, { credentials: 'include' }),
      ]);
      const sub = await subRes.json();
      const pro = await proRes.json();
      setIsPremium(sub.isPremium);
      setSubscriptionStatus(sub.status);
      setTrialDaysLeft(sub.trialDaysLeft);
      setIsPro(!!pro.isPro);
      setProStatus(pro.proStatus || 'none');
      setProTrialDaysLeft(pro.proTrialDaysLeft ?? null);
      setProTrialExpiresAt(pro.proTrialExpiresAt ?? null);
    } catch {
      setIsPremium(false);
      setSubscriptionStatus('free');
      setTrialDaysLeft(null);
      setIsPro(false);
      setProStatus('none');
      setProTrialDaysLeft(null);
      setProTrialExpiresAt(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [user, checkSubscriptionStatus]);

  const startTrial = async () => {
    try {
      const response = await fetch(`${API}/subscription/start-trial`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to start trial');
      setIsPremium(true);
      setSubscriptionStatus('trial');
      setTrialDaysLeft(data.trialDaysLeft);
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Pro add-on: 22-day no-card free trial — kicks off HRV features without
  // a checkout round-trip. On success, local state flips to `trial` so the
  // UI can update immediately without re-polling.
  const startProTrial = async () => {
    try {
      const res = await fetch(`${API}/pro/start-trial`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start Pro trial');
      setIsPro(true);
      setProStatus('trial');
      setProTrialDaysLeft(data.proTrialDaysLeft);
      setProTrialExpiresAt(data.proTrialExpiresAt ?? null);
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Open Stripe Checkout for the $59 Pro add-on. Returns { url } on success —
  // caller redirects; on return the `?payment=success&pro=1` query fires the
  // post-pay polling flow that already exists for Premium.
  const beginProCheckout = async () => {
    try {
      const res = await fetch(`${API}/payments/pro-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout');
      return { success: true, url: data.url, sessionId: data.session_id };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const value = {
    isPremium,
    subscriptionStatus,
    trialDaysLeft,
    isPro,
    proStatus,
    proTrialDaysLeft,
    proTrialExpiresAt,
    loading,
    startTrial,
    startProTrial,
    beginProCheckout,
    checkSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
